/**
 * Versionless Repair Intelligence host contract.
 *
 * Preferred public surface: `window.RepairIntelligence`.
 * Legacy alias: `window.ri2` (deprecated; kept for backward compatibility).
 *
 * Host-facing CSS vars, body classes, mount IDs, storage keys, hash params,
 * and CustomEvents use the `repairintelligence` namespace, with dual write/read
 * against the legacy `ri2` names during the deprecation window.
 */

import {ROOT} from "./routing"

export const PAGE_CONTEXT_EVENT = "repairintelligence:pagecontext"
export const LEGACY_PAGE_CONTEXT_EVENT = "ri2:pagecontext"

export const HOST_EVENTS = {
  pageContext: PAGE_CONTEXT_EVENT,
  collapse: "repairintelligence:collapse",
  expand: "repairintelligence:expand",
  userExpanded: "repairintelligence:userExpanded",
  userCollapsed: "repairintelligence:userCollapsed",
} as const

export const LEGACY_HOST_EVENTS = {
  pageContext: LEGACY_PAGE_CONTEXT_EVENT,
  collapse: "ri2:collapse",
  expand: "ri2:expand",
  userExpanded: "ri2:userExpanded",
  userCollapsed: "ri2:userCollapsed",
} as const

export type HostEventName = keyof typeof HOST_EVENTS

/** Preferred + legacy body class for slideout layout (hosts may style either). */
export const SLIDEOUT_BODY_CLASS = "repairintelligence-slideout-active"
export const LEGACY_SLIDEOUT_BODY_CLASS = "ri2-slideout-active"

export const SLIDEOUT_WIDTH_VAR = "--repairintelligence-slideout-width"
export const LEGACY_SLIDEOUT_WIDTH_VAR = "--ri2-slideout-width"

export const SLIDEOUT_TOP_VAR = "--repairintelligence-slideout-top"
export const LEGACY_SLIDEOUT_TOP_VAR = "--ri2-slideout-top"

export const MOUNT_IDS = {
  widget: "repairintelligence-widget",
  legacyWidget: "ri2-widget",
  widgetRoot: "repairintelligence-widget-root",
  legacyWidgetRoot: "ri2-widget-root",
  slideoutHost: "repairintelligence-slideout-host",
  legacySlideoutHost: "ri2-slideout-host",
  entryApp: "repairintelligence-entry-app",
  legacyEntryApp: "ri2-entry-app",
  root: "repairintelligence-root",
  legacyRoot: "ri2-root",
} as const

export const STORAGE_KEYS = {
  caseId: "repairintelligence-widget-caseId",
  legacyCaseId: "ri2-widget-caseId",
  startersDismissed: "repairintelligence-widget-startersDismissed",
  legacyStartersDismissed: "ri2-widget-startersDismissed",
  lastSeenIdPrefix: "repairintelligence-widget-lastSeenId:",
  legacyLastSeenIdPrefix: "ri2-widget-lastSeenId:",
  lastUnreadIdPrefix: "repairintelligence-widget-lastUnreadId:",
  legacyLastUnreadIdPrefix: "ri2-widget-lastUnreadId:",
} as const

export const HASH_CASE_PARAM = "repairintelligence-case"
export const LEGACY_HASH_CASE_PARAM = "ri2-case"

/**
 * Makes the preferred public host bridge available while preserving
 * integrations that still use `window.ri2`.
 */
export const normalizeHostBridge = (): void => {
  if (window.RepairIntelligence) {
    window.ri2 = window.RepairIntelligence
    return
  }

  if (window.ri2) {
    window.RepairIntelligence = window.ri2
  }
}

export const getHostBridge = (): RepairIntelligenceHostBridge | undefined => {
  normalizeHostBridge()
  return window.RepairIntelligence ?? window.ri2
}

/**
 * Merge session-start intake with the current page context for case creation.
 * `pageContext` wins on overlapping keys so SPA navigation stays authoritative
 * for browse fields; intake-only fields (complaint, age, …) still flow through.
 */
export const getMergedInitPageContext = (): Ri2PageContext | undefined => {
  const bridge = getHostBridge()
  const intake = bridge?.initialIntake
  const pageContext = bridge?.pageContext
  if (!intake && !pageContext) return undefined

  return {
    ...(intake ?? {}),
    ...(pageContext ?? {}),
  }
}

/**
 * Tenant-owned opaque correlation ID from the host bridge.
 * Empty/whitespace is treated as absent. Not part of intake/pageContext.
 */
export const getExternalSessionId = (): string | undefined => {
  const value = getHostBridge()?.externalSessionId
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed === "" ? undefined : trimmed
}

export const assignHostBridge = (
  bridge: RepairIntelligenceHostBridge,
): void => {
  window.RepairIntelligence = bridge
  window.ri2 = bridge
}

export const patchHostBridge = (
  patch: Partial<RepairIntelligenceHostBridge>,
): RepairIntelligenceHostBridge => {
  const bridge = {
    ...(getHostBridge() ?? {}),
    ...patch,
  } as RepairIntelligenceHostBridge
  assignHostBridge(bridge)
  return bridge
}

export const dispatchHostEvent = (
  name: HostEventName,
  init?: EventInit & {detail?: unknown},
): void => {
  const preferred = HOST_EVENTS[name]
  const legacy = LEGACY_HOST_EVENTS[name]

  if (init && "detail" in init) {
    window.dispatchEvent(new CustomEvent(preferred, init))
    window.dispatchEvent(new CustomEvent(legacy, init))
    return
  }

  window.dispatchEvent(new Event(preferred, init))
  window.dispatchEvent(new Event(legacy, init))
}

/** Dual-fire page-context updates (preferred + legacy event names). */
export const dispatchPageContextChange = (ctx: Ri2PageContext | null): void => {
  dispatchHostEvent("pageContext", {detail: ctx})
}

export const addHostEventListener = (
  name: HostEventName,
  handler: EventListener,
): (() => void) => {
  const preferred = HOST_EVENTS[name]
  const legacy = LEGACY_HOST_EVENTS[name]
  window.addEventListener(preferred, handler)
  window.addEventListener(legacy, handler)
  return (): void => {
    window.removeEventListener(preferred, handler)
    window.removeEventListener(legacy, handler)
  }
}

export const applySlideoutHostLayout = (widthPx: number): void => {
  document.body.classList.add(SLIDEOUT_BODY_CLASS, LEGACY_SLIDEOUT_BODY_CLASS)
  document.documentElement.style.setProperty(SLIDEOUT_WIDTH_VAR, `${widthPx}px`)
  document.documentElement.style.setProperty(
    LEGACY_SLIDEOUT_WIDTH_VAR,
    `${widthPx}px`,
  )
}

export const removeSlideoutHostLayout = (): void => {
  document.body.classList.remove(
    SLIDEOUT_BODY_CLASS,
    LEGACY_SLIDEOUT_BODY_CLASS,
  )
  document.documentElement.style.removeProperty(SLIDEOUT_WIDTH_VAR)
  document.documentElement.style.removeProperty(LEGACY_SLIDEOUT_WIDTH_VAR)
}

export const setSlideoutTopVar = (value: string): void => {
  document.documentElement.style.setProperty(SLIDEOUT_TOP_VAR, value)
  document.documentElement.style.setProperty(LEGACY_SLIDEOUT_TOP_VAR, value)
}

export const removeSlideoutTopVar = (): void => {
  document.documentElement.style.removeProperty(SLIDEOUT_TOP_VAR)
  document.documentElement.style.removeProperty(LEGACY_SLIDEOUT_TOP_VAR)
}

export const getElementByMountId = (
  preferredId: string,
  legacyId: string,
): HTMLElement | null =>
  document.getElementById(preferredId) ?? document.getElementById(legacyId)

export const getWidgetMountElement = (): HTMLElement | null =>
  getElementByMountId(MOUNT_IDS.widget, MOUNT_IDS.legacyWidget)

export const getSlideoutHostElement = (): HTMLElement | null =>
  getElementByMountId(MOUNT_IDS.slideoutHost, MOUNT_IDS.legacySlideoutHost)

export const getEntryAppElement = (): HTMLElement | null =>
  getElementByMountId(MOUNT_IDS.entryApp, MOUNT_IDS.legacyEntryApp)

export const createWidgetRootElement = (): HTMLDivElement => {
  const el = document.createElement("div")
  el.id = MOUNT_IDS.widgetRoot
  // Legacy id attribute kept as data for older CSS/selectors during deprecation.
  el.dataset.legacyId = MOUNT_IDS.legacyWidgetRoot
  document.body.appendChild(el)
  return el
}

export const createFullscreenRootElement = (): HTMLDivElement => {
  const el = document.createElement("div")
  el.id = MOUNT_IDS.root
  el.dataset.legacyId = MOUNT_IDS.legacyRoot
  document.body.appendChild(el)
  return el
}

export const dualStorageGet = (
  primaryKey: string,
  legacyKey: string,
): string | null => {
  try {
    return localStorage.getItem(primaryKey) ?? localStorage.getItem(legacyKey)
  } catch {
    return null
  }
}

export const dualStorageSet = (
  primaryKey: string,
  legacyKey: string,
  value: string,
): void => {
  try {
    localStorage.setItem(primaryKey, value)
    localStorage.setItem(legacyKey, value)
  } catch {
    // Quota / private mode — ignore
  }
}

export const dualStorageRemove = (
  primaryKey: string,
  legacyKey: string,
): void => {
  try {
    localStorage.removeItem(primaryKey)
    localStorage.removeItem(legacyKey)
  } catch {
    // ignore
  }
}

const HASH_CASE_PARAM_PATTERN = new RegExp(
  `(?:^#?|&?)(?:${HASH_CASE_PARAM}|${LEGACY_HASH_CASE_PARAM})=[^&]*`,
  "g",
)

/** HashRouter case paths: `#/ri/{uuid}` (preferred) or `#/ri2/{uuid}` (legacy). */
const HASH_CASE_ROUTE_PATTERN =
  /(?:^#\/?)(?:ri2?)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$|\?)/i

export const getHashCaseId = (): string | null => {
  const routeMatch = window.location.hash.match(HASH_CASE_ROUTE_PATTERN)
  if (routeMatch?.[1]) return routeMatch[1]

  const match = window.location.hash.match(
    new RegExp(
      `(?:^#|&)(?:${HASH_CASE_PARAM}|${LEGACY_HASH_CASE_PARAM})=([^&]+)`,
    ),
  )
  return match ? match[1] : null
}

export const setHashCaseId = (caseId: string): void => {
  const existingHash = window.location.hash.replace(HASH_CASE_PARAM_PATTERN, "")
  const base = existingHash.replace(/^#/, "").replace(/^&/, "")
  const newHash = base
    ? `${base}&${HASH_CASE_PARAM}=${caseId}`
    : `${HASH_CASE_PARAM}=${caseId}`
  history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}#${newHash}`,
  )
}

export const clearHashCaseId = (): void => {
  const newHash = window.location.hash
    .replace(HASH_CASE_PARAM_PATTERN, "")
    .replace(/^#?&/, "")
    .replace(/^#/, "")
  history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}${
      newHash ? `#${newHash}` : ""
    }`,
  )
}

/** Share / deep-link URL using the preferred HashRouter path (`#/ri/{caseId}`). */
export const buildCaseShareUrl = (caseId: string): string =>
  `${window.location.origin}${window.location.pathname}${window.location.search}#${ROOT}/${caseId}`

export const lastSeenMessageIdKeys = (
  caseId: string,
): {primary: string; legacy: string} => ({
  primary: `${STORAGE_KEYS.lastSeenIdPrefix}${caseId}`,
  legacy: `${STORAGE_KEYS.legacyLastSeenIdPrefix}${caseId}`,
})

export const lastUnreadMessageIdKeys = (
  caseId: string,
): {primary: string; legacy: string} => ({
  primary: `${STORAGE_KEYS.lastUnreadIdPrefix}${caseId}`,
  legacy: `${STORAGE_KEYS.legacyLastUnreadIdPrefix}${caseId}`,
})
